import React, { useState, useEffect } from 'react';
import {
  FiSearch,
  FiLoader,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiRefreshCw,
  FiInfo,
  FiCheckCircle,
  FiAlertCircle,
  FiEye,
  FiCreditCard,
  FiPercent,
  FiPlus,
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

const Cash = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showOnlinePaymentModal, setShowOnlinePaymentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [activeStatus, setActiveStatus] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [billingTypes, setBillingTypes] = useState([]);
  const [selectedBillingType, setSelectedBillingType] = useState('all');
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [processingPaymentId, setProcessingPaymentId] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [showAddBillingModal, setShowAddBillingModal] = useState(false);
  const [billingFormData, setBillingFormData] = useState({
    bookingId: '',
    baseAmount: '',
    taxAmount: '',
    discount: '',
    billingType: 'Food Order',
  });

  const rowsPerPage = 40;
  const maxVisiblePages = 5;
  const navigate = useNavigate();

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        toast.error('Error loading Razorpay script. Please try again.');
      };
      document.body.appendChild(script);
    };

    if (!window.Razorpay && !razorpayLoaded) {
      loadRazorpayScript();
    }

    fetchData();
  }, []);

  const handleAddBilling = async () => {
    try {
      const payload = {
        booking_id: billingFormData.bookingId,
        base_amount: parseFloat(billingFormData.baseAmount),
        tax_amount: parseFloat(billingFormData.taxAmount),
        discount: parseFloat(billingFormData.discount),
        billing_type: billingFormData.billingType,
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/add/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add billing record');
      }

      toast.success(
        <div className="flex items-center gap-2">
          <FiCheckCircle className="text-green-500" size={18} />
          <div>
            <p className="font-medium">Billing Record Added!</p>
            <p className="text-sm text-gray-500">Billing type: {billingFormData.billingType}</p>
          </div>
        </div>,
        { autoClose: 5000 }
      );

      setShowAddBillingModal(false);
      setBillingFormData({
        bookingId: '',
        baseAmount: '',
        taxAmount: '',
        discount: '',
        billingType: 'Food Order',
      });
      fetchData(true);
    } catch (error) {
      console.error('Error adding billing:', error);
      toast.error(
        <div className="flex items-center gap-2">
          <FiAlertCircle className="text-red-500" size={18} />
          <span>{error.message || 'Failed to add billing record'}</span>
        </div>,
        { autoClose: 5000 }
      );
    }
  };

  // ── Shared formatter: turns a single aq_billing-summary response into a row ──
  const formatSummary = (summary) => {
    const billingDetails = summary.billing_details || [];

    const totalBilled = billingDetails.reduce((sum, b) => sum + (b.total_amount || 0), 0);
    const totalPaid = billingDetails.reduce((sum, b) => sum + (b.paid_amount || 0), 0);
    const netPayable = totalBilled - totalPaid;
    const totalDiscount = billingDetails.reduce((sum, b) => sum + (b.discount || 0), 0);

    const positiveBillings = billingDetails.filter((b) => b.total_amount > 0);
    const grossBilled = positiveBillings.reduce((sum, b) => sum + (b.total_amount || 0), 0);

    const unpaidBillings = billingDetails.filter(
      (b) => !b.is_paid && b.total_amount > 0 && !b.is_credit
    );
    const payableBillings = billingDetails.filter(
      (b) => !b.is_paid && b.total_amount > 0 && !b.is_credit
    );
    const paidBillings = billingDetails.filter((b) => b.is_paid);

    return {
      id: summary.booking?.booking_id,
      bookingId: summary.booking?.booking_id,
      orderId: summary.orderid,
      guestName:
        (summary.guest_details?.first_name || '') + ' ' + (summary.guest_details?.last_name || ''),
      guestPhone: summary.guest_details?.phone,
      bookingStatus: summary.booking?.booking_status,
      totalBilled,
      grossBilled,
      totalPaid,
      netPayable,
      totalDiscount,
      isFullyPaid: netPayable <= 0,
      hasAdjustments: billingDetails.some((b) => b.billing_type?.includes('Manual Adjustment')),
      totalAdjustmentAmount: billingDetails
        .filter((b) => b.billing_type?.includes('Manual Adjustment'))
        .reduce((sum, b) => sum + (b.total_amount || 0), 0),
      billings: billingDetails,
      payableBillings,
      unpaidBillings,
      paidBillings,
      unpaidBillingIds: unpaidBillings.map((b) => b.billing_id),
      allPayments: billingDetails.flatMap((b) => b.payment_details || []),
      date: summary.booking?.checkin_date,
      roomType: summary.booking?.room_type,
      checkinDate: summary.booking?.checkin_date,
      checkoutDate: summary.booking?.checkout_date,
    };
  };

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      // ── ONE call: get every booking with billings in a single response ──
      const summaryResponse = await fetch(`${BQ_BASE_URL}/bq/api/guest/aq_billing-summary/`, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });

      if (!summaryResponse.ok) {
        throw new Error('Failed to fetch billing summaries');
      }

      const payload = await summaryResponse.json();

      // ── Backend returns ONE aggregated object (same shape as before) ──
      // Extract every booking id + guest + billing set by walking billing_details
      // and grouping by booking? No — the new backend keeps a single flattened
      // response. The clean way: use the by_room array OR just treat the whole
      // response as one "row" set.

      // ⚠️ If your backend returns a LIST of summaries (one per booking),
      // the response is an array and this branch handles it.
      // If your backend returns a single aggregated object (previous design),
      // wrap it in an array so downstream code stays unchanged.

      let summaries = [];
      if (Array.isArray(payload)) {
        summaries = payload;
      } else if (Array.isArray(payload.summaries)) {
        // Alternative shape: { summaries: [...] }
        summaries = payload.summaries;
      } else if (payload.billing_summary?.by_room && payload.billing_summary.by_room.length > 1) {
        // Aggregated object that contains multiple rooms → split into
        // per-booking rows so the table shows one row per booking.
        // We use billing_summary.by_room as the source of truth for
        // which bookings exist, and slice billing_details accordingly.
        //
        // For simplicity, if the backend returns one object per booking
        // (which is what /aq_billing-summary/ now does WITHOUT order_id),
        // we expect this branch NOT to hit. Kept as a safety net.
        summaries = [payload];
      } else {
        summaries = [payload];
      }

      const formattedData = summaries.map((summary) => formatSummary(summary));

      setAllData(formattedData);
      setFilteredData(formattedData);

      const uniqueBillingTypes = [
        ...new Set(
          summaries.flatMap((summary) =>
            (summary.billing_details || []).map((b) => b.billing_type).filter(Boolean)
          )
        ),
      ].sort();

      setBillingTypes(uniqueBillingTypes);

      if (showRefresh) {
        toast.success(
          <div className="flex items-center gap-2">
            <FiCheckCircle className="text-green-500" size={18} />
            <span>Data refreshed successfully!</span>
          </div>
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(
        <div className="flex items-center gap-2">
          <FiAlertCircle className="text-red-500" size={18} />
          <span>Failed to fetch billing data</span>
        </div>
      );
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const handleSearch = async (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);

    setIsSearching(true);

    setTimeout(() => {
      filterData(searchValue, activeStatus, timeRange, selectedBillingType);
      setIsSearching(false);
    }, 300);
  };

  const handleTimeRangeChange = (e) => {
    const range = e.target.value;
    setTimeRange(range);
    filterData(searchTerm, activeStatus, range, selectedBillingType);
  };

  const handleBillingTypeChange = (e) => {
    const billingType = e.target.value;
    setSelectedBillingType(billingType);
    filterData(searchTerm, activeStatus, timeRange, billingType);
  };

  const filterData = (searchValue, status, timeRange, billingType) => {
    let filtered = allData.filter(
      (item) =>
        item.guestName.toLowerCase().includes(searchValue) ||
        item.orderId.toLowerCase().includes(searchValue) ||
        item.bookingId?.toLowerCase().includes(searchValue)
    );

    if (status !== 'all') {
      filtered = filtered.filter((item) =>
        status === 'active' ? item.bookingStatus === 'Hard' : item.bookingStatus === 'Soft'
      );
    }

    if (timeRange !== 'all') {
      const currentDate = new Date();
      const startDate = new Date();

      if (timeRange === 'day') {
        startDate.setDate(currentDate.getDate() - 1);
      } else if (timeRange === 'week') {
        startDate.setDate(currentDate.getDate() - 7);
      } else if (timeRange === 'month') {
        startDate.setMonth(currentDate.getMonth() - 1);
      }

      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate;
      });
    }

    if (billingType !== 'all') {
      filtered = filtered.filter((item) =>
        item.billings.some((b) => b.billing_type === billingType)
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    filterData('', activeStatus, timeRange, selectedBillingType);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(totalPages);

  const getVisiblePages = () => {
    let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handlePayAllCash = (booking) => {
    setSelectedBooking(booking);
    setSelectedBilling(null);
    setShowModal(true);
  };

  const handlePayAllOnline = (booking) => {
    setSelectedBooking(booking);
    setSelectedBilling(null);
    setShowOnlinePaymentModal(true);
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowViewModal(true);
  };

  const handlePaySingleCash = (billing, booking) => {
    setSelectedBooking(booking);
    setSelectedBilling(billing);
    setShowModal(true);
  };

  const handlePaySingleOnline = (billing, booking) => {
    setSelectedBooking(booking);
    setSelectedBilling(billing);
    setShowOnlinePaymentModal(true);
  };

  const handleContinueCash = async () => {
    const isPayAll = !selectedBilling;

    if (isPayAll) {
      const netPayable = selectedBooking.netPayable;

      if (netPayable <= 0) {
        toast.info(
          <div className="flex items-center gap-2">
            <FiInfo className="text-blue-500" size={18} />
            <span>No pending amount to pay</span>
          </div>
        );
        setShowModal(false);
        setSelectedBilling(null);
        return;
      }

      setProcessingPaymentId(selectedBooking.id);

      try {
        const mainBilling = selectedBooking.billings.find(
          (b) => b.total_amount > 0 && !b.is_credit && !b.is_paid
        );

        if (!mainBilling) {
          toast.error(
            <div className="flex items-center gap-2">
              <FiAlertCircle className="text-red-500" size={18} />
              <span>No pending billing found to pay against</span>
            </div>
          );
          setShowModal(false);
          setSelectedBilling(null);
          return;
        }

        const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            booking_id: selectedBooking.bookingId,
            billing_id: mainBilling.billing_id,
            amount: netPayable,
            receipt_number:
              'RECEIPT-' +
              new Date()
                .toISOString()
                .replace(/[^0-9]/g, '')
                .slice(0, 14),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          const errorMessage = data.detail || data.message || 'Payment failed';
          throw new Error(errorMessage);
        }

        toast.success(
          <div className="flex items-center gap-2">
            <FiCheckCircle className="text-green-500" size={18} />
            <div>
              <p className="font-medium">Payment Successful!</p>
              <p className="text-sm text-gray-500">
                Amount: ₹{netPayable.toFixed(2)} (after adjustments)
              </p>
            </div>
          </div>,
          { autoClose: 5000 }
        );

        fetchData(true);
        setShowModal(false);
        setSelectedBilling(null);
      } catch (error) {
        console.error('Error making cash payment:', error);

        let errorMessage = error.message;
        let errorTitle = 'Payment Failed';

        if (error.message.includes('Full payment has already been made')) {
          errorTitle = 'Already Paid';
          errorMessage = 'Payment already completed. No further payment required.';
        } else if (error.message.includes('Payment amount exceeds')) {
          const remainingMatch = error.message.match(/(\d+\.?\d*)/);
          const remaining = remainingMatch ? remainingMatch[0] : 'unknown';
          errorTitle = 'Amount Exceeds Balance';
          errorMessage = `Amount exceeds balance. Remaining: ₹${remaining}`;
        } else if (error.message.includes('Billing record not found')) {
          errorTitle = 'Record Not Found';
          errorMessage = 'Billing record not found. Please refresh and try again.';
        } else if (error.message.includes('Payment creation failed')) {
          errorTitle = 'Processing Failed';
          errorMessage = 'Payment processing failed. Please try again.';
        }

        toast.error(
          <div className="flex items-center gap-2">
            <FiAlertCircle className="text-red-500" size={18} />
            <div>
              <p className="font-medium">{errorTitle}</p>
              <p className="text-sm text-gray-500">{errorMessage}</p>
            </div>
          </div>,
          { autoClose: 5000 }
        );
      } finally {
        setProcessingPaymentId(null);
      }
    } else {
      const billingIds = [selectedBilling.billing_id];
      setProcessingPaymentId(selectedBooking.id);

      try {
        const hasAdjustments = selectedBooking.billings.some(
          (b) => b.billing_type?.includes('Manual Adjustment') && b.total_amount < 0
        );

        let amountToPay = parseFloat(
          selectedBilling.total_amount || selectedBilling.final_amount || 0
        );
        let billingIdToPay = selectedBilling.billing_id;

        if (hasAdjustments && selectedBilling.total_amount > 0 && !selectedBilling.is_credit) {
          const mainBilling = selectedBooking.billings.find(
            (b) => b.total_amount > 0 && !b.is_credit
          );

          if (mainBilling && selectedBilling.billing_id === mainBilling.billing_id) {
            amountToPay = selectedBooking.netPayable;
            billingIdToPay = mainBilling.billing_id;
          }
        }

        if (!amountToPay || amountToPay <= 0) {
          toast.error(
            <div className="flex items-center gap-2">
              <FiAlertCircle className="text-red-500" size={18} />
              <span>Invalid payment amount: ₹{amountToPay}</span>
            </div>
          );
          setShowModal(false);
          setSelectedBilling(null);
          return;
        }

        const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            booking_id: selectedBooking.bookingId,
            billing_id: billingIdToPay,
            amount: amountToPay,
            receipt_number:
              'RECEIPT-' +
              new Date()
                .toISOString()
                .replace(/[^0-9]/g, '')
                .slice(0, 14),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          const errorMessage = data.detail || data.message || 'Payment failed';
          throw new Error(errorMessage);
        }

        toast.success(
          <div className="flex items-center gap-2">
            <FiCheckCircle className="text-green-500" size={18} />
            <div>
              <p className="font-medium">Payment Successful!</p>
              <p className="text-sm text-gray-500">
                Amount: ₹{amountToPay.toFixed(2)}
                {hasAdjustments && amountToPay < selectedBilling.total_amount && (
                  <span className="text-xs text-gray-400 block">
                    (Net amount after ₹{Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}{' '}
                    adjustment)
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-400">Billing: {selectedBilling.billing_type}</p>
            </div>
          </div>,
          { autoClose: 5000 }
        );

        fetchData(true);
        setShowModal(false);
        setSelectedBilling(null);
      } catch (error) {
        console.error('Error making cash payment:', error);

        let errorMessage = error.message;
        let errorTitle = 'Payment Failed';

        if (error.message.includes('Full payment has already been made')) {
          errorTitle = 'Already Paid';
          errorMessage = 'Payment already completed. No further payment required.';
        } else if (error.message.includes('Payment amount exceeds')) {
          const remainingMatch = error.message.match(/(\d+\.?\d*)/);
          const remaining = remainingMatch ? remainingMatch[0] : 'unknown';
          errorTitle = 'Amount Exceeds Balance';
          errorMessage = `Amount exceeds balance. Remaining: ₹${remaining}`;
        } else if (error.message.includes('Billing record not found')) {
          errorTitle = 'Record Not Found';
          errorMessage = 'Billing record not found. Please refresh and try again.';
        } else if (error.message.includes('Payment creation failed')) {
          errorTitle = 'Processing Failed';
          errorMessage = 'Payment processing failed. Please try again.';
        }

        toast.error(
          <div className="flex items-center gap-2">
            <FiAlertCircle className="text-red-500" size={18} />
            <div>
              <p className="font-medium">{errorTitle}</p>
              <p className="text-sm text-gray-500">{errorMessage}</p>
            </div>
          </div>,
          { autoClose: 5000 }
        );
      } finally {
        setProcessingPaymentId(null);
      }
    }
  };

  const handleContinueOnline = async () => {
    if (!window.Razorpay) {
      toast.error(
        <div className="flex items-center gap-2">
          <FiAlertCircle className="text-red-500" size={18} />
          <span>Payment system is still loading. Please try again.</span>
        </div>
      );
      return;
    }

    const isPayAll = !selectedBilling;
    let billingIds = [];

    if (isPayAll) {
      const netPayable = selectedBooking.netPayable;

      if (netPayable <= 0) {
        toast.info('No pending amount to pay');
        return;
      }

      const mainBilling = selectedBooking.billings.find(
        (b) => b.total_amount > 0 && !b.is_credit && !b.is_paid
      );

      if (!mainBilling) {
        toast.error('No pending billing found to pay against');
        return;
      }

      billingIds = [mainBilling.billing_id];
    } else {
      const hasAdjustments = selectedBooking.billings.some(
        (b) => b.billing_type?.includes('Manual Adjustment') && b.total_amount < 0
      );

      if (hasAdjustments && selectedBilling.total_amount > 0 && !selectedBilling.is_credit) {
        const mainBilling = selectedBooking.billings.find(
          (b) => b.total_amount > 0 && !b.is_credit
        );

        if (mainBilling && selectedBilling.billing_id === mainBilling.billing_id) {
          billingIds = [mainBilling.billing_id];
        } else {
          billingIds = [selectedBilling.billing_id];
        }
      } else {
        billingIds = [selectedBilling.billing_id];
      }
    }

    setProcessingPaymentId(selectedBooking.id);

    try {
      const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order_multiple?bookingid=${selectedBooking.bookingId}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(billingIds),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || `Payment order creation failed: ${response.statusText}`
        );
      }

      const orderData = await response.json();

      if (orderData && orderData.razorpay_order_id) {
        const totalAmount = isPayAll
          ? selectedBooking.netPayable
          : parseFloat(selectedBilling.total_amount);

        const options = {
          key: REACT_APP_RAZORPAY_KEY,
          amount: Math.round(totalAmount * 100),
          currency: 'INR',
          name: 'Pagoda Hotel',
          description: isPayAll ? 'Multiple Billings Payment' : selectedBilling.billing_type,
          order_id: orderData.razorpay_order_id,
          handler: async function (response) {
            const paymentDetails = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              billing_ids: billingIds,
              booking_id: selectedBooking.bookingId,
            };

            try {
              const verificationResponse = await fetch(
                `${BQ_BASE_URL}/bq/api/razorpay/verify_payment_multiple`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                  },
                  body: JSON.stringify(paymentDetails),
                }
              );

              if (verificationResponse.ok) {
                toast.success(
                  <div className="flex items-center gap-2">
                    <FiCheckCircle className="text-green-500" size={18} />
                    <div>
                      <p className="font-medium">Payment Successful!</p>
                      <p className="text-sm text-gray-500">
                        Transaction ID: {response.razorpay_payment_id}
                      </p>
                    </div>
                  </div>,
                  { autoClose: 5000 }
                );

                fetchData(true);
                setShowOnlinePaymentModal(false);
                setSelectedBilling(null);
              } else {
                toast.error(
                  <div className="flex items-center gap-2">
                    <FiAlertCircle className="text-red-500" size={18} />
                    <span>Payment verification failed. Please contact support.</span>
                  </div>
                );
              }
            } catch (error) {
              console.error('Verification error:', error);
              toast.error(
                <div className="flex items-center gap-2">
                  <FiAlertCircle className="text-red-500" size={18} />
                  <span>Error verifying payment. Please try again.</span>
                </div>
              );
            }
          },
          modal: {
            ondismiss: function () {
              setProcessingPaymentId(null);
            },
          },
          prefill: {
            name: selectedBooking.guestName,
            contact: selectedBooking.guestPhone?.toString(),
          },
          theme: {
            color: '#3399cc',
          },
          notes: {
            bookingId: selectedBooking.bookingId,
            billingIds: billingIds.join(','),
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(
        <div className="flex items-center gap-2">
          <FiAlertCircle className="text-red-500" size={18} />
          <span>{error.message || 'Error processing payment. Please try again.'}</span>
        </div>
      );
    } finally {
      setProcessingPaymentId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getReservationStatus = (status) => {
    switch (status?.toLowerCase()) {
      case 'hard':
        return 'Checked In';
      case 'soft':
        return 'Reserved';
      case 'checkedout':
        return 'Checked Out';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status || 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'hard':
        return 'bg-green-100 text-green-800';
      case 'soft':
        return 'bg-yellow-100 text-yellow-800';
      case 'checkedout':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const TableSkeleton = () => (
    <>
      {[...Array(9)].map((_, index) => (
        <tr key={index} className="animate-pulse">
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-8"></div>
          </td>
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </td>
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
          </td>
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-28"></div>
          </td>
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-16"></div>
          </td>
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-20"></div>
          </td>
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-16"></div>
          </td>
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
          </td>
          <td className="py-3 px-4 border">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
          </td>
          <td className="py-2 px-1 border">
            <div className="h-8 bg-gray-200 rounded w-32"></div>
          </td>
        </tr>
      ))}
    </>
  );

  const InfoModal = ({ message, onClose }) => (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-[400px] transform transition-all">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-full">
            <FiInfo className="text-blue-600" size={24} />
          </div>
          <h2 className="text-xl text-black font-bold">Information</h2>
        </div>
        <p className="text-gray-700 mb-6">{message}</p>
        <div className="flex justify-end">
          <button
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition font-medium"
            onClick={onClose}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );

  const ViewDetailsModal = ({ booking, onClose }) => (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-[900px] max-h-[80vh] overflow-y-auto transform transition-all">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <FiEye className="text-blue-600" size={24} />
            </div>
            <h2 className="text-xl text-black font-bold">Billing Details</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX size={24} />
          </button>
        </div>

        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Guest Name</p>
              <p className="font-medium">{booking.guestName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Reservation No.</p>
              <p className="font-mono font-medium">{booking.orderId}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Reservation Status</p>
              <span
                className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(booking.bookingStatus)}`}
              >
                {getReservationStatus(booking.bookingStatus)}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Room Type</p>
              <p className="font-medium">{booking.roomType || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Check In</p>
              <p className="font-medium">{formatDate(booking.checkinDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Check Out</p>
              <p className="font-medium">{formatDate(booking.checkoutDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="font-medium">₹{booking.grossBilled?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Amount Paid</p>
              <p className="font-medium text-green-600">₹{booking.totalPaid?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Net Amount Due</p>
              <p className="font-medium text-blue-600">₹{booking.netPayable?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Discount</p>
              <p className="font-medium text-purple-600">₹{booking.totalDiscount?.toFixed(2)}</p>
            </div>
          </div>
          {booking.hasAdjustments && booking.totalAdjustmentAmount < 0 && (
            <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
              <p className="text-sm text-yellow-700">
                <span className="font-medium">Note:</span> Manual adjustments have been applied. Net
                amount due is ₹{booking.netPayable?.toFixed(2)} (after ₹
                {Math.abs(booking.totalAdjustmentAmount).toFixed(2)} adjustment).
              </p>
            </div>
          )}
          {booking.hasAdjustments && booking.totalAdjustmentAmount > 0 && (
            <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Note:</span> Additional adjustments have been applied.
                Net amount due is ₹{booking.netPayable?.toFixed(2)} (after ₹
                {booking.totalAdjustmentAmount.toFixed(2)} adjustment).
              </p>
            </div>
          )}
        </div>

        <h3 className="font-semibold mb-2">Billings</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-left">Billing Type</th>
                <th className="px-2 py-2 text-right">Amount</th>
                <th className="px-2 py-2 text-right">Tax</th>
                <th className="px-2 py-2 text-right">Discount</th>
                <th className="px-2 py-2 text-right">Total</th>
                <th className="px-2 py-2 text-center">Status</th>
                <th className="px-2 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {booking.billings
                .filter((b) => b.total_amount > 0 && !b.is_credit)
                .map((billing) => {
                  const isPaid = billing.is_paid;

                  return (
                    <tr key={billing.billing_id} className="border-b hover:bg-gray-50">
                      <td className="px-2 py-2">{billing.billing_type}</td>
                      <td className="px-2 py-2 text-right">
                        ₹{billing.base_amount?.toFixed(2) || 0}
                      </td>
                      <td className="px-2 py-2 text-right">
                        ₹{billing.tax_amount?.toFixed(2) || 0}
                      </td>
                      <td className="px-2 py-2 text-right text-purple-600">
                        ₹{billing.discount?.toFixed(2) || 0}
                      </td>
                      <td className="px-2 py-2 text-right font-medium">
                        ₹{billing.total_amount?.toFixed(2) || 0}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            isPaid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {isPaid ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        {isPaid ? (
                          <span className="text-gray-500 text-xs italic bg-gray-100 px-2 py-1 rounded">
                            No actions
                          </span>
                        ) : booking.netPayable <= 0 ? (
                          <span className="text-green-600 text-xs font-medium bg-green-50 px-2 py-1 rounded">
                            Fully Paid
                          </span>
                        ) : (
                          <div className="flex gap-1 justify-center">
                            <button
                              className="px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800"
                              onClick={() => {
                                onClose();
                                handlePaySingleCash(billing, booking);
                              }}
                            >
                              Cash
                            </button>
                            <button
                              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              onClick={() => {
                                onClose();
                                handlePaySingleOnline(billing, booking);
                              }}
                            >
                              Digital
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              {booking.billings.filter((b) => b.total_amount > 0 && !b.is_credit).length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-gray-500">
                    No pending billings
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {booking.billings.some((b) => b.total_amount < 0 && b.is_credit) && (
          <>
            <h3 className="font-semibold mt-4 mb-2 text-orange-600">Adjustments / Credits</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-orange-50">
                  <tr>
                    <th className="px-2 py-2 text-left">Billing Type</th>
                    <th className="px-2 py-2 text-right">Amount</th>
                    <th className="px-2 py-2 text-right">Tax</th>
                    <th className="px-2 py-2 text-right">Discount</th>
                    <th className="px-2 py-2 text-right">Total</th>
                    <th className="px-2 py-2 text-center">Status</th>
                    <th className="px-2 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.billings
                    .filter((b) => b.total_amount < 0 && b.is_credit)
                    .map((billing) => {
                      const isPaid = billing.is_paid;

                      return (
                        <tr
                          key={billing.billing_id}
                          className="border-b hover:bg-orange-50 bg-orange-50/30"
                        >
                          <td className="px-2 py-2 text-orange-700">{billing.billing_type}</td>
                          <td className="px-2 py-2 text-right text-orange-700">
                            ₹{billing.base_amount?.toFixed(2) || 0}
                          </td>
                          <td className="px-2 py-2 text-right text-orange-700">
                            ₹{billing.tax_amount?.toFixed(2) || 0}
                          </td>
                          <td className="px-2 py-2 text-right text-purple-600">
                            ₹{billing.discount?.toFixed(2) || 0}
                          </td>
                          <td className="px-2 py-2 text-right font-medium text-red-600">
                            ₹{billing.total_amount?.toFixed(2) || 0}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                isPaid
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {isPaid ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-center">
                            <span className="text-gray-500 text-xs italic bg-gray-100 px-2 py-1 rounded">
                              Credit applied
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {booking.allPayments && booking.allPayments.length > 0 && (
          <>
            <h3 className="font-semibold mt-4 mb-2">Payment History</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-2 text-left">Date</th>
                    <th className="px-2 py-2 text-left">Method</th>
                    <th className="px-2 py-2 text-right">Amount</th>
                    <th className="px-2 py-2 text-center">Status</th>
                    <th className="px-2 py-2 text-center">Transaction ID</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.allPayments.map((payment, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-2 py-2">
                        {formatDate(payment.date || payment.payment_date)}
                      </td>
                      <td className="px-2 py-2">{payment.payment_method || 'Cash'}</td>
                      <td className="px-2 py-2 text-right">
                        ₹{parseFloat(payment.amount).toFixed(2)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            payment.status === 'Success'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {payment.status || 'Success'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center font-mono text-xs">
                        {payment.transaction_id || payment.payment_id || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="flex justify-end mt-4">
          <button
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition font-medium"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex flex-col">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {showInfoModal && <InfoModal message={infoMessage} onClose={() => setShowInfoModal(false)} />}

      {showViewModal && selectedBooking && (
        <ViewDetailsModal
          booking={selectedBooking}
          onClose={() => {
            setShowViewModal(false);
            setSelectedBooking(null);
          }}
        />
      )}

      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
        <div className="flex space-x-6">
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-operations')
            }
            className="btn"
          >
            Front Desk Operations
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/guest-managment')
            }
            className="btn"
          >
            Guest Management
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-roomservices')
            }
            className="btn"
          >
            Hospitality Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-amenities')
            }
            className="btn"
          >
            Facility Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/billing-payments')
            }
            className="btn text-black font-bold"
          >
            Billing & Payments
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/refunds-adjustments')
            }
            className="btn"
          >
            Refunds & Adjustments
          </button>
        </div>
      </header>

      <div className="text-center mb-4">
        <div className="inline-block">
          <h1 className="text-2xl font-bold text-center text-black">Billings & Payments</h1>
          <p className="text-sm font-poppins text-center text-gray-600 max-w-2xl">
            Make payments via cash or digital methods, view transaction history, and manage payments
            seamlessly.
          </p>
          <hr className="border-gray-400 mt-2 w-60 mx-auto" />
        </div>
      </div>

      <div className="mb-4 flex flex-col md:flex-row items-center gap-4">
        <div className="relative w-full md:w-2/5">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by guest name or reservation number"
              value={searchTerm}
              onChange={handleSearch}
              className="p-3 pl-12 pr-10 h-12 border w-full border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
            <FiSearch className="absolute top-3.5 left-4 text-gray-500" size={20} />
            {searchTerm && !isSearching && (
              <button
                onClick={clearSearch}
                className="absolute top-3.5 right-3 text-gray-500 hover:text-gray-700 transition-colors"
                title="Clear search"
              >
                <FiX size={20} />
              </button>
            )}
            {isSearching && (
              <div className="absolute top-3.5 right-3">
                <FiLoader className="animate-spin text-gray-500" size={18} />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end ml-auto">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-3 h-12 px-4 border rounded-lg shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title="Refresh data"
          >
            <FiRefreshCw className={`${isRefreshing ? 'animate-spin' : ''}`} size={18} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          <select
            onChange={handleTimeRangeChange}
            value={timeRange}
            className="p-3 h-12 border rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="all">All Time</option>
            <option value="day">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>

          <select
            onChange={handleBillingTypeChange}
            value={selectedBillingType}
            className="p-3 h-12 border rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="all">All Billing Types</option>
            {billingTypes.map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto bg-white shadow-md rounded-lg">
        <table className="min-w-full bg-white border border-gray-300">
          <thead>
            <tr className="bg-gray-200 text-black font-semibold text-base">
              <th className="py-3 px-1 border border-gray-300">Sr No.</th>
              <th className="py-3 px-4 border border-gray-300">Guest</th>
              <th className="py-3 px-4 border border-gray-300">Reservation No.</th>
              <th className="py-3 px-4 border border-gray-300">Total Amount</th>
              <th className="py-3 px-4 border border-gray-300">Amount Paid</th>
              <th className="py-3 px-4 border border-gray-300">Discount</th>
              <th className="py-3 px-4 border border-gray-300">Amount Due</th>
              <th className="py-3 px-4 border border-gray-300">Reservation Status</th>
              <th className="py-3 px-4 border border-gray-300">Date</th>
              <th className="py-3 px-2 border border-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableSkeleton />
            ) : currentRows.length > 0 ? (
              currentRows.map((item, index) => {
                const isProcessing = processingPaymentId === item.id;
                const hasUnpaid = item.unpaidBillingIds.length > 0 && item.netPayable > 0;

                return (
                  <tr key={item.id} className="hover:bg-gray-50 border transition-colors">
                    <td className="py-2 px-2 border text-center">{indexOfFirstRow + index + 1}</td>
                    <td className="py-2 px-4 border text-sm font-medium">{item.guestName}</td>
                    <td className="py-2 px-4 border font-mono text-sm">{item.orderId}</td>
                    <td className="py-2 px-4 border font-medium">
                      ₹{item.grossBilled?.toFixed(2)}
                    </td>
                    <td className="py-2 px-4 border text-green-600 font-medium">
                      ₹{item.totalPaid?.toFixed(2)}
                    </td>
                    <td className="py-2 px-4 border text-purple-600 font-medium">
                      ₹{item.totalDiscount?.toFixed(2)}
                    </td>
                    <td className="py-2 px-4 border font-medium text-blue-600">
                      ₹{item.netPayable?.toFixed(2)}
                    </td>
                    <td className="py-2 px-4 border">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(item.bookingStatus)}`}
                      >
                        {getReservationStatus(item.bookingStatus)}
                      </span>
                    </td>
                    <td className="py-2 px-4 border text-sm">{formatDate(item.date)}</td>
                    <td className="py-2 px-1 border">
                      {item.netPayable > 0 && hasUnpaid ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                            <button
                              className="px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 flex-1 flex items-center justify-center gap-1"
                              onClick={() => handlePayAllCash(item)}
                              disabled={isProcessing}
                            >
                              {isProcessing ? (
                                <FiLoader className="animate-spin" size={12} />
                              ) : (
                                <FaRupeeSign size={12} />
                              )}
                              <span>Pay All</span>
                            </button>
                            <button
                              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex-1 flex items-center justify-center gap-1"
                              onClick={() => handlePayAllOnline(item)}
                              disabled={isProcessing}
                            >
                              {isProcessing ? (
                                <FiLoader className="animate-spin" size={12} />
                              ) : (
                                <FiCreditCard size={12} />
                              )}
                              <span>Digital</span>
                            </button>
                          </div>
                          <button
                            className="px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 flex items-center justify-center gap-1"
                            onClick={() => handleViewDetails(item)}
                          >
                            <FiEye size={12} />
                            <span>View</span>
                          </button>
                        </div>
                      ) : item.totalBilled > 0 ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-green-600 font-medium text-xs bg-green-50 px-2 py-1 rounded text-center border border-green-200">
                            Fully Paid
                          </span>
                          <button
                            className="px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 flex items-center justify-center gap-1"
                            onClick={() => handleViewDetails(item)}
                          >
                            <FiEye size={12} />
                            <span>View</span>
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-500 text-xs italic bg-gray-100 px-2 py-1 rounded block text-center">
                          No action
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-12">
                  <div className="flex flex-col items-center">
                    <div className="bg-gray-100 rounded-full p-4 mb-3">
                      <FiSearch size={24} className="text-gray-500" />
                    </div>
                    <p className="text-gray-500 text-lg mb-2">No billing records found</p>
                    {searchTerm && (
                      <button
                        onClick={clearSearch}
                        className="text-black hover:underline text-sm flex items-center gap-1"
                      >
                        <FiX size={14} />
                        Clear search and filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && filteredData.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
          <div className="text-sm text-gray-600">
            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredData.length)} of{' '}
            {filteredData.length} entries
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={handleFirstPage}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg transition ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
              title="First page"
            >
              <FiChevronsLeft size={18} />
            </button>

            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg transition ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
              title="Previous page"
            >
              <FiChevronLeft size={18} />
            </button>

            {getVisiblePages().map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-10 h-10 rounded-lg font-medium transition ${
                  currentPage === page ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                {page}
              </button>
            ))}

            {totalPages > maxVisiblePages && currentPage < totalPages - 2 && (
              <span className="text-gray-500">...</span>
            )}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg transition ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
              title="Next page"
            >
              <FiChevronRight size={18} />
            </button>

            <button
              onClick={handleLastPage}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg transition ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
              title="Last page"
            >
              <FiChevronsRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Cash Payment Modal */}
      {showModal && selectedBooking && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[470px] transform transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-100 p-2 rounded-full">
                <FiCheckCircle className="text-green-600" size={24} />
              </div>
              <h2 className="text-xl text-black font-bold">Confirm Cash Payment</h2>
            </div>

            <div className="space-y-3 mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between">
                <p className="text-gray-600">Guest:</p>
                <p className="text-gray-800 font-medium">{selectedBooking.guestName}</p>
              </div>

              <div className="flex justify-between">
                <p className="text-gray-600">Reservation No.:</p>
                <p className="text-gray-800 font-mono font-medium">{selectedBooking.orderId}</p>
              </div>

              {selectedBilling ? (
                <>
                  <div className="flex justify-between">
                    <p className="text-gray-600">Billing Type:</p>
                    <p className="text-gray-800 font-medium">{selectedBilling.billing_type}</p>
                  </div>

                  {selectedBooking.hasAdjustments &&
                    selectedBooking.totalAdjustmentAmount < 0 &&
                    selectedBilling.total_amount > 0 &&
                    !selectedBilling.is_credit && (
                      <>
                        <div className="flex justify-between">
                          <p className="text-gray-600">Gross Amount:</p>
                          <p className="text-gray-800 font-medium">
                            ₹{selectedBilling.total_amount?.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex justify-between">
                          <p className="text-gray-600">Adjustments:</p>
                          <p className="text-red-600 font-medium">
                            -₹
                            {Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}
                          </p>
                        </div>
                        <div className="flex justify-between border-t border-gray-300 pt-2">
                          <p className="text-gray-700 font-medium">Net Amount Due:</p>
                          <p className="text-green-600 font-bold text-lg">
                            ₹{selectedBooking.netPayable?.toFixed(2)}
                          </p>
                        </div>
                      </>
                    )}

                  {(!selectedBooking.hasAdjustments ||
                    selectedBilling.total_amount < 0 ||
                    selectedBilling.is_credit) && (
                    <div className="flex justify-between border-t border-gray-300 pt-2">
                      <p className="text-gray-700 font-medium">Amount Due:</p>
                      <p className="text-green-600 font-bold text-lg">
                        ₹{selectedBilling.total_amount?.toFixed(2)}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <p className="text-gray-600">Gross Amount:</p>
                    <p className="text-gray-800 font-medium">
                      ₹{selectedBooking.grossBilled?.toFixed(2)}
                    </p>
                  </div>
                  {selectedBooking.hasAdjustments && selectedBooking.totalAdjustmentAmount < 0 && (
                    <div className="flex justify-between">
                      <p className="text-gray-600">Adjustments:</p>
                      <p className="text-red-600 font-medium">
                        -₹
                        {Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}
                      </p>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-300 pt-2">
                    <p className="text-gray-700 font-medium">Total Amount Due:</p>
                    <p className="text-green-600 font-bold text-lg">
                      ₹{selectedBooking.netPayable?.toFixed(2)}
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
                onClick={() => {
                  setShowModal(false);
                  setSelectedBilling(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] justify-center"
                onClick={handleContinueCash}
                disabled={processingPaymentId === selectedBooking.id}
              >
                {processingPaymentId === selectedBooking.id ? (
                  <>
                    <FiLoader className="animate-spin" size={18} />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Confirm Payment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Billing Record Modal */}
      {showAddBillingModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[500px] transform transition-all max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-blue-100 p-2 rounded-full">
                <FiPlus className="text-blue-600" size={24} />
              </div>
              <h2 className="text-xl text-black font-bold">Add Billing Record</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter booking ID"
                  value={billingFormData.bookingId}
                  onChange={(e) =>
                    setBillingFormData({
                      ...billingFormData,
                      bookingId: e.target.value,
                    })
                  }
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  placeholder="Enter base amount"
                  value={billingFormData.baseAmount}
                  onChange={(e) =>
                    setBillingFormData({
                      ...billingFormData,
                      baseAmount: e.target.value,
                    })
                  }
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Amount</label>
                <input
                  type="number"
                  placeholder="Enter tax amount"
                  value={billingFormData.taxAmount}
                  onChange={(e) =>
                    setBillingFormData({
                      ...billingFormData,
                      taxAmount: e.target.value,
                    })
                  }
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
                <input
                  type="number"
                  placeholder="Enter discount amount"
                  value={billingFormData.discount}
                  onChange={(e) =>
                    setBillingFormData({
                      ...billingFormData,
                      discount: e.target.value,
                    })
                  }
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Type <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={billingFormData.billingType}
                  disabled
                  className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This field is pre-set to "Food Order" and will be sent to the backend.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
                onClick={() => {
                  setShowAddBillingModal(false);
                  setBillingFormData({
                    bookingId: '',
                    baseAmount: '',
                    taxAmount: '',
                    discount: '',
                    billingType: 'Food Order',
                  });
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition font-medium flex items-center gap-2"
                onClick={handleAddBilling}
              >
                <FiPlus size={16} />
                Add Record
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Online Payment Modal */}
      {showOnlinePaymentModal && selectedBooking && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[470px] transform transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-2 rounded-full">
                <FiInfo className="text-blue-600" size={24} />
              </div>
              <h2 className="text-xl text-black font-bold">Confirm Online Payment</h2>
            </div>

            <div className="space-y-3 mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between">
                <p className="text-gray-600">Guest:</p>
                <p className="text-gray-800 font-medium">{selectedBooking.guestName}</p>
              </div>

              <div className="flex justify-between">
                <p className="text-gray-600">Reservation No.:</p>
                <p className="text-gray-800 font-mono font-medium">{selectedBooking.orderId}</p>
              </div>

              {selectedBilling ? (
                <>
                  <div className="flex justify-between">
                    <p className="text-gray-600">Billing Type:</p>
                    <p className="text-gray-800 font-medium">{selectedBilling.billing_type}</p>
                  </div>

                  {selectedBooking.hasAdjustments &&
                    selectedBooking.totalAdjustmentAmount < 0 &&
                    selectedBilling.total_amount > 0 &&
                    !selectedBilling.is_credit && (
                      <>
                        <div className="flex justify-between">
                          <p className="text-gray-600">Gross Amount:</p>
                          <p className="text-gray-800 font-medium">
                            ₹{selectedBilling.total_amount?.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex justify-between">
                          <p className="text-gray-600">Adjustments:</p>
                          <p className="text-red-600 font-medium">
                            -₹
                            {Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}
                          </p>
                        </div>
                        <div className="flex justify-between border-t border-gray-300 pt-2">
                          <p className="text-gray-700 font-medium">Net Amount Due:</p>
                          <p className="text-green-600 font-bold text-lg">
                            ₹{selectedBooking.netPayable?.toFixed(2)}
                          </p>
                        </div>
                      </>
                    )}

                  {(!selectedBooking.hasAdjustments ||
                    selectedBilling.total_amount < 0 ||
                    selectedBilling.is_credit) && (
                    <div className="flex justify-between border-t border-gray-300 pt-2">
                      <p className="text-gray-700 font-medium">Amount Due:</p>
                      <p className="text-green-600 font-bold text-lg">
                        ₹{selectedBilling.total_amount?.toFixed(2)}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <p className="text-gray-600">Gross Amount:</p>
                    <p className="text-gray-800 font-medium">
                      ₹{selectedBooking.grossBilled?.toFixed(2)}
                    </p>
                  </div>
                  {selectedBooking.hasAdjustments && selectedBooking.totalAdjustmentAmount < 0 && (
                    <div className="flex justify-between">
                      <p className="text-gray-600">Adjustments:</p>
                      <p className="text-red-600 font-medium">
                        -₹
                        {Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}
                      </p>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-300 pt-2">
                    <p className="text-gray-700 font-medium">Total Amount Due:</p>
                    <p className="text-green-600 font-bold text-lg">
                      ₹{selectedBooking.netPayable?.toFixed(2)}
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
                onClick={() => {
                  setShowOnlinePaymentModal(false);
                  setSelectedBilling(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] justify-center"
                onClick={handleContinueOnline}
                disabled={processingPaymentId === selectedBooking.id}
              >
                {processingPaymentId === selectedBooking.id ? (
                  <>
                    <FiLoader className="animate-spin" size={18} />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Pay Now'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cash;

// import React, { useState, useEffect } from 'react';
// import {
//   FiSearch,
//   FiLoader,
//   FiX,
//   FiChevronLeft,
//   FiChevronRight,
//   FiChevronsLeft,
//   FiChevronsRight,
//   FiRefreshCw,
//   FiInfo,
//   FiCheckCircle,
//   FiAlertCircle,
//   FiEye,
//   FiCreditCard,
//   FiPercent,
//   FiPlus,
// } from 'react-icons/fi';
// import { FaRupeeSign } from 'react-icons/fa';
// import { useNavigate } from 'react-router-dom';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
// const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// const Cash = () => {
//   const [allData, setAllData] = useState([]);
//   const [filteredData, setFilteredData] = useState([]);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [currentPage, setCurrentPage] = useState(1);
//   const [showModal, setShowModal] = useState(false);
//   const [showOnlinePaymentModal, setShowOnlinePaymentModal] = useState(false);
//   const [showViewModal, setShowViewModal] = useState(false);
//   const [selectedBooking, setSelectedBooking] = useState(null);
//   const [selectedBilling, setSelectedBilling] = useState(null);
//   const [activeStatus, setActiveStatus] = useState('all');
//   const [timeRange, setTimeRange] = useState('all');
//   const [billingTypes, setBillingTypes] = useState([]);
//   const [selectedBillingType, setSelectedBillingType] = useState('all');
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [isLoading, setIsLoading] = useState(true);
//   const [isSearching, setIsSearching] = useState(false);
//   const [isRefreshing, setIsRefreshing] = useState(false);
//   const [processingPaymentId, setProcessingPaymentId] = useState(null);
//   const [showInfoModal, setShowInfoModal] = useState(false);
//   const [infoMessage, setInfoMessage] = useState('');
//   const [showAddBillingModal, setShowAddBillingModal] = useState(false);
//   const [billingFormData, setBillingFormData] = useState({
//     bookingId: '',
//     baseAmount: '',
//     taxAmount: '',
//     discount: '',
//     billingType: 'Food Order',
//   });

//   const rowsPerPage = 15;
//   const maxVisiblePages = 5;
//   const navigate = useNavigate();

//   useEffect(() => {
//     const loadRazorpayScript = () => {
//       const script = document.createElement('script');
//       script.src = 'https://checkout.razorpay.com/v1/checkout.js';
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => {
//         toast.error('Error loading Razorpay script. Please try again.');
//       };
//       document.body.appendChild(script);
//     };

//     if (!window.Razorpay && !razorpayLoaded) {
//       loadRazorpayScript();
//     }

//     fetchData();
//   }, []);

//   const handleAddBilling = async () => {
//     try {
//       const payload = {
//         booking_id: billingFormData.bookingId,
//         base_amount: parseFloat(billingFormData.baseAmount),
//         tax_amount: parseFloat(billingFormData.taxAmount),
//         discount: parseFloat(billingFormData.discount),
//         billing_type: billingFormData.billingType,
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/add/`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify(payload),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to add billing record');
//       }

//       toast.success(
//         <div className="flex items-center gap-2">
//           <FiCheckCircle className="text-green-500" size={18} />
//           <div>
//             <p className="font-medium">Billing Record Added!</p>
//             <p className="text-sm text-gray-500">Billing type: {billingFormData.billingType}</p>
//           </div>
//         </div>,
//         { autoClose: 5000 }
//       );

//       setShowAddBillingModal(false);
//       setBillingFormData({
//         bookingId: '',
//         baseAmount: '',
//         taxAmount: '',
//         discount: '',
//         billingType: 'Food Order',
//       });
//       fetchData(true);
//     } catch (error) {
//       console.error('Error adding billing:', error);
//       toast.error(
//         <div className="flex items-center gap-2">
//           <FiAlertCircle className="text-red-500" size={18} />
//           <span>{error.message || 'Failed to add billing record'}</span>
//         </div>,
//         { autoClose: 5000 }
//       );
//     }
//   };

//   const fetchData = async (showRefresh = false) => {
//     if (showRefresh) {
//       setIsRefreshing(true);
//     } else {
//       setIsLoading(true);
//     }

//     try {
//       const bookingsResponse = await fetch(`${BQ_BASE_URL}/bq/api/bookings/`, {
//         method: 'GET',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (!bookingsResponse.ok) {
//         throw new Error('Failed to fetch bookings');
//       }

//       const bookingsData = await bookingsResponse.json();
//       const orders = bookingsData.bookings || [];

//       const billingDataPromises = orders.map(async (order) => {
//         try {
//           const summaryResponse = await fetch(
//             `${BQ_BASE_URL}/bq/api/guest/aq_billing-summary/?order_id=${order.orderid}`,
//             {
//               method: 'GET',
//               headers: {
//                 accept: 'application/json',
//               },
//             }
//           );

//           if (summaryResponse.ok) {
//             const summaryData = await summaryResponse.json();
//             return summaryData;
//           }
//           return null;
//         } catch (error) {
//           console.error(`Error fetching summary for ${order.orderid}:`, error);
//           return null;
//         }
//       });

//       const billingSummaries = await Promise.all(billingDataPromises);
//       const validSummaries = billingSummaries.filter((summary) => summary !== null);

//       const formattedData = validSummaries.map((summary) => {
//         const billingDetails = summary.billing_details || [];

//         // Calculate totals
//         const totalBilled = billingDetails.reduce((sum, b) => sum + (b.total_amount || 0), 0);

//         const totalPaid = billingDetails.reduce((sum, b) => sum + (b.paid_amount || 0), 0);

//         const netPayable = totalBilled - totalPaid;

//         const totalDiscount = billingDetails.reduce((sum, b) => sum + (b.discount || 0), 0);

//         // Get positive billings for gross amount
//         const positiveBillings = billingDetails.filter((b) => b.total_amount > 0);
//         const grossBilled = positiveBillings.reduce((sum, b) => sum + (b.total_amount || 0), 0);

//         // Get unpaid positive billings (for payment actions)
//         const unpaidBillings = billingDetails.filter(
//           (b) => !b.is_paid && b.total_amount > 0 && !b.is_credit
//         );

//         const payableBillings = billingDetails.filter(
//           (b) => !b.is_paid && b.total_amount > 0 && !b.is_credit
//         );

//         const paidBillings = billingDetails.filter((b) => b.is_paid);

//         return {
//           id: summary.booking?.booking_id,
//           bookingId: summary.booking?.booking_id,
//           orderId: summary.orderid,
//           guestName: summary.guest_details?.first_name + ' ' + summary.guest_details?.last_name,
//           guestPhone: summary.guest_details?.phone,
//           bookingStatus: summary.booking?.booking_status,
//           totalBilled: totalBilled,
//           grossBilled: grossBilled,
//           totalPaid: totalPaid,
//           netPayable: netPayable,
//           totalDiscount: totalDiscount,
//           isFullyPaid: netPayable <= 0,
//           hasAdjustments: billingDetails.some((b) => b.billing_type?.includes('Manual Adjustment')),
//           totalAdjustmentAmount: billingDetails
//             .filter((b) => b.billing_type?.includes('Manual Adjustment'))
//             .reduce((sum, b) => sum + (b.total_amount || 0), 0),
//           billings: billingDetails,
//           payableBillings: payableBillings,
//           unpaidBillings: unpaidBillings,
//           paidBillings: paidBillings,
//           unpaidBillingIds: unpaidBillings.map((b) => b.billing_id),
//           allPayments: billingDetails.flatMap((b) => b.payment_details || []),
//           date: summary.booking?.checkin_date,
//           roomType: summary.booking?.room_type,
//           checkinDate: summary.booking?.checkin_date,
//           checkoutDate: summary.booking?.checkout_date,
//         };
//       });

//       setAllData(formattedData);
//       setFilteredData(formattedData);

//       const uniqueBillingTypes = [
//         ...new Set(
//           validSummaries.flatMap((summary) =>
//             (summary.billing_details || []).map((b) => b.billing_type).filter(Boolean)
//           )
//         ),
//       ].sort();

//       setBillingTypes(uniqueBillingTypes);

//       if (showRefresh) {
//         toast.success(
//           <div className="flex items-center gap-2">
//             <FiCheckCircle className="text-green-500" size={18} />
//             <span>Data refreshed successfully!</span>
//           </div>
//         );
//       }
//     } catch (error) {
//       console.error('Error fetching data:', error);
//       toast.error(
//         <div className="flex items-center gap-2">
//           <FiAlertCircle className="text-red-500" size={18} />
//           <span>Failed to fetch billing data</span>
//         </div>
//       );
//     } finally {
//       setIsLoading(false);
//       setIsRefreshing(false);
//     }
//   };

//   const handleRefresh = () => {
//     fetchData(true);
//   };

//   const handleSearch = async (e) => {
//     const searchValue = e.target.value.toLowerCase();
//     setSearchTerm(searchValue);

//     setIsSearching(true);

//     setTimeout(() => {
//       filterData(searchValue, activeStatus, timeRange, selectedBillingType);
//       setIsSearching(false);
//     }, 300);
//   };

//   const handleTimeRangeChange = (e) => {
//     const range = e.target.value;
//     setTimeRange(range);
//     filterData(searchTerm, activeStatus, range, selectedBillingType);
//   };

//   const handleBillingTypeChange = (e) => {
//     const billingType = e.target.value;
//     setSelectedBillingType(billingType);
//     filterData(searchTerm, activeStatus, timeRange, billingType);
//   };

//   const filterData = (searchValue, status, timeRange, billingType) => {
//     let filtered = allData.filter(
//       (item) =>
//         item.guestName.toLowerCase().includes(searchValue) ||
//         item.orderId.toLowerCase().includes(searchValue) ||
//         item.bookingId?.toLowerCase().includes(searchValue)
//     );

//     if (status !== 'all') {
//       filtered = filtered.filter((item) =>
//         status === 'active' ? item.bookingStatus === 'Hard' : item.bookingStatus === 'Soft'
//       );
//     }

//     if (timeRange !== 'all') {
//       const currentDate = new Date();
//       const startDate = new Date();

//       if (timeRange === 'day') {
//         startDate.setDate(currentDate.getDate() - 1);
//       } else if (timeRange === 'week') {
//         startDate.setDate(currentDate.getDate() - 7);
//       } else if (timeRange === 'month') {
//         startDate.setMonth(currentDate.getMonth() - 1);
//       }

//       filtered = filtered.filter((item) => {
//         const itemDate = new Date(item.date);
//         return itemDate >= startDate;
//       });
//     }

//     if (billingType !== 'all') {
//       filtered = filtered.filter((item) =>
//         item.billings.some((b) => b.billing_type === billingType)
//       );
//     }

//     setFilteredData(filtered);
//     setCurrentPage(1);
//   };

//   const clearSearch = () => {
//     setSearchTerm('');
//     filterData('', activeStatus, timeRange, selectedBillingType);
//   };

//   const totalPages = Math.ceil(filteredData.length / rowsPerPage);
//   const indexOfLastRow = currentPage * rowsPerPage;
//   const indexOfFirstRow = indexOfLastRow - rowsPerPage;
//   const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

//   const handlePageChange = (page) => setCurrentPage(page);
//   const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
//   const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
//   const handleFirstPage = () => setCurrentPage(1);
//   const handleLastPage = () => setCurrentPage(totalPages);

//   const getVisiblePages = () => {
//     let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
//     let end = Math.min(totalPages, start + maxVisiblePages - 1);

//     if (end - start + 1 < maxVisiblePages) {
//       start = Math.max(1, end - maxVisiblePages + 1);
//     }

//     return Array.from({ length: end - start + 1 }, (_, i) => start + i);
//   };

//   const handlePayAllCash = (booking) => {
//     setSelectedBooking(booking);
//     setSelectedBilling(null); // Important: Set to null for Pay All
//     setShowModal(true);
//   };

//   const handlePayAllOnline = (booking) => {
//     setSelectedBooking(booking);
//     setSelectedBilling(null); // Important: Set to null for Pay All
//     setShowOnlinePaymentModal(true);
//   };

//   const handleViewDetails = (booking) => {
//     setSelectedBooking(booking);
//     setShowViewModal(true);
//   };

//   const handlePaySingleCash = (billing, booking) => {
//     setSelectedBooking(booking);
//     setSelectedBilling(billing);
//     setShowModal(true);
//   };

//   const handlePaySingleOnline = (billing, booking) => {
//     setSelectedBooking(booking);
//     setSelectedBilling(billing);
//     setShowOnlinePaymentModal(true);
//   };

//   const handleContinueCash = async () => {
//     const isPayAll = !selectedBilling;

//     if (isPayAll) {
//       const netPayable = selectedBooking.netPayable;

//       if (netPayable <= 0) {
//         toast.info(
//           <div className="flex items-center gap-2">
//             <FiInfo className="text-blue-500" size={18} />
//             <span>No pending amount to pay</span>
//           </div>
//         );
//         setShowModal(false);
//         setSelectedBilling(null);
//         return;
//       }

//       setProcessingPaymentId(selectedBooking.id);

//       try {
//         const mainBilling = selectedBooking.billings.find(
//           (b) => b.total_amount > 0 && !b.is_credit && !b.is_paid
//         );

//         if (!mainBilling) {
//           toast.error(
//             <div className="flex items-center gap-2">
//               <FiAlertCircle className="text-red-500" size={18} />
//               <span>No pending billing found to pay against</span>
//             </div>
//           );
//           setShowModal(false);
//           setSelectedBilling(null);
//           return;
//         }

//         const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             booking_id: selectedBooking.bookingId,
//             billing_id: mainBilling.billing_id,
//             amount: netPayable,
//             receipt_number:
//               'RECEIPT-' +
//               new Date()
//                 .toISOString()
//                 .replace(/[^0-9]/g, '')
//                 .slice(0, 14),
//           }),
//         });

//         const data = await response.json();

//         if (!response.ok) {
//           const errorMessage = data.detail || data.message || 'Payment failed';
//           throw new Error(errorMessage);
//         }

//         toast.success(
//           <div className="flex items-center gap-2">
//             <FiCheckCircle className="text-green-500" size={18} />
//             <div>
//               <p className="font-medium">Payment Successful!</p>
//               <p className="text-sm text-gray-500">
//                 Amount: ₹{netPayable.toFixed(2)} (after adjustments)
//               </p>
//             </div>
//           </div>,
//           { autoClose: 5000 }
//         );

//         fetchData(true);
//         setShowModal(false);
//         setSelectedBilling(null);
//       } catch (error) {
//         console.error('Error making cash payment:', error);

//         let errorMessage = error.message;
//         let errorTitle = 'Payment Failed';

//         if (error.message.includes('Full payment has already been made')) {
//           errorTitle = 'Already Paid';
//           errorMessage = 'Payment already completed. No further payment required.';
//         } else if (error.message.includes('Payment amount exceeds')) {
//           const remainingMatch = error.message.match(/(\d+\.?\d*)/);
//           const remaining = remainingMatch ? remainingMatch[0] : 'unknown';
//           errorTitle = 'Amount Exceeds Balance';
//           errorMessage = `Amount exceeds balance. Remaining: ₹${remaining}`;
//         } else if (error.message.includes('Billing record not found')) {
//           errorTitle = 'Record Not Found';
//           errorMessage = 'Billing record not found. Please refresh and try again.';
//         } else if (error.message.includes('Payment creation failed')) {
//           errorTitle = 'Processing Failed';
//           errorMessage = 'Payment processing failed. Please try again.';
//         }

//         toast.error(
//           <div className="flex items-center gap-2">
//             <FiAlertCircle className="text-red-500" size={18} />
//             <div>
//               <p className="font-medium">{errorTitle}</p>
//               <p className="text-sm text-gray-500">{errorMessage}</p>
//             </div>
//           </div>,
//           { autoClose: 5000 }
//         );
//       } finally {
//         setProcessingPaymentId(null);
//       }
//     } else {
//       // Single billing payment
//       const billingIds = [selectedBilling.billing_id];
//       setProcessingPaymentId(selectedBooking.id);

//       try {
//         // Check if there are adjustments
//         const hasAdjustments = selectedBooking.billings.some(
//           (b) => b.billing_type?.includes('Manual Adjustment') && b.total_amount < 0
//         );

//         let amountToPay = parseFloat(
//           selectedBilling.total_amount || selectedBilling.final_amount || 0
//         );
//         let billingIdToPay = selectedBilling.billing_id;

//         // If there are adjustments and this is the main billing, pay the net amount
//         if (hasAdjustments && selectedBilling.total_amount > 0 && !selectedBilling.is_credit) {
//           // Find the main billing
//           const mainBilling = selectedBooking.billings.find(
//             (b) => b.total_amount > 0 && !b.is_credit
//           );

//           if (mainBilling && selectedBilling.billing_id === mainBilling.billing_id) {
//             // Pay the net amount against the main billing
//             amountToPay = selectedBooking.netPayable;
//             billingIdToPay = mainBilling.billing_id;
//           }
//         }

//         // Validate amount
//         if (!amountToPay || amountToPay <= 0) {
//           toast.error(
//             <div className="flex items-center gap-2">
//               <FiAlertCircle className="text-red-500" size={18} />
//               <span>Invalid payment amount: ₹{amountToPay}</span>
//             </div>
//           );
//           setShowModal(false);
//           setSelectedBilling(null);
//           return;
//         }

//         const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             booking_id: selectedBooking.bookingId,
//             billing_id: billingIdToPay,
//             amount: amountToPay,
//             receipt_number:
//               'RECEIPT-' +
//               new Date()
//                 .toISOString()
//                 .replace(/[^0-9]/g, '')
//                 .slice(0, 14),
//           }),
//         });

//         const data = await response.json();

//         if (!response.ok) {
//           const errorMessage = data.detail || data.message || 'Payment failed';
//           throw new Error(errorMessage);
//         }

//         toast.success(
//           <div className="flex items-center gap-2">
//             <FiCheckCircle className="text-green-500" size={18} />
//             <div>
//               <p className="font-medium">Payment Successful!</p>
//               <p className="text-sm text-gray-500">
//                 Amount: ₹{amountToPay.toFixed(2)}
//                 {hasAdjustments && amountToPay < selectedBilling.total_amount && (
//                   <span className="text-xs text-gray-400 block">
//                     (Net amount after ₹{Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}{' '}
//                     adjustment)
//                   </span>
//                 )}
//               </p>
//               <p className="text-xs text-gray-400">Billing: {selectedBilling.billing_type}</p>
//             </div>
//           </div>,
//           { autoClose: 5000 }
//         );

//         fetchData(true);
//         setShowModal(false);
//         setSelectedBilling(null);
//       } catch (error) {
//         console.error('Error making cash payment:', error);

//         let errorMessage = error.message;
//         let errorTitle = 'Payment Failed';

//         if (error.message.includes('Full payment has already been made')) {
//           errorTitle = 'Already Paid';
//           errorMessage = 'Payment already completed. No further payment required.';
//         } else if (error.message.includes('Payment amount exceeds')) {
//           const remainingMatch = error.message.match(/(\d+\.?\d*)/);
//           const remaining = remainingMatch ? remainingMatch[0] : 'unknown';
//           errorTitle = 'Amount Exceeds Balance';
//           errorMessage = `Amount exceeds balance. Remaining: ₹${remaining}`;
//         } else if (error.message.includes('Billing record not found')) {
//           errorTitle = 'Record Not Found';
//           errorMessage = 'Billing record not found. Please refresh and try again.';
//         } else if (error.message.includes('Payment creation failed')) {
//           errorTitle = 'Processing Failed';
//           errorMessage = 'Payment processing failed. Please try again.';
//         }

//         toast.error(
//           <div className="flex items-center gap-2">
//             <FiAlertCircle className="text-red-500" size={18} />
//             <div>
//               <p className="font-medium">{errorTitle}</p>
//               <p className="text-sm text-gray-500">{errorMessage}</p>
//             </div>
//           </div>,
//           { autoClose: 5000 }
//         );
//       } finally {
//         setProcessingPaymentId(null);
//       }
//     }
//   };

//   const handleContinueOnline = async () => {
//     if (!window.Razorpay) {
//       toast.error(
//         <div className="flex items-center gap-2">
//           <FiAlertCircle className="text-red-500" size={18} />
//           <span>Payment system is still loading. Please try again.</span>
//         </div>
//       );
//       return;
//     }

//     const isPayAll = !selectedBilling;
//     let billingIds = [];

//     if (isPayAll) {
//       const netPayable = selectedBooking.netPayable;

//       if (netPayable <= 0) {
//         toast.info('No pending amount to pay');
//         return;
//       }

//       const mainBilling = selectedBooking.billings.find(
//         (b) => b.total_amount > 0 && !b.is_credit && !b.is_paid
//       );

//       if (!mainBilling) {
//         toast.error('No pending billing found to pay against');
//         return;
//       }

//       billingIds = [mainBilling.billing_id];
//     } else {
//       // Check if there are adjustments for single payment
//       const hasAdjustments = selectedBooking.billings.some(
//         (b) => b.billing_type?.includes('Manual Adjustment') && b.total_amount < 0
//       );

//       if (hasAdjustments && selectedBilling.total_amount > 0 && !selectedBilling.is_credit) {
//         const mainBilling = selectedBooking.billings.find(
//           (b) => b.total_amount > 0 && !b.is_credit
//         );

//         if (mainBilling && selectedBilling.billing_id === mainBilling.billing_id) {
//           billingIds = [mainBilling.billing_id];
//         } else {
//           billingIds = [selectedBilling.billing_id];
//         }
//       } else {
//         billingIds = [selectedBilling.billing_id];
//       }
//     }

//     setProcessingPaymentId(selectedBooking.id);

//     try {
//       const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order_multiple?bookingid=${selectedBooking.bookingId}`;

//       const response = await fetch(apiUrl, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify(billingIds),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(
//           errorData.detail || `Payment order creation failed: ${response.statusText}`
//         );
//       }

//       const orderData = await response.json();

//       if (orderData && orderData.razorpay_order_id) {
//         const totalAmount = isPayAll
//           ? selectedBooking.netPayable
//           : parseFloat(selectedBilling.total_amount);

//         const options = {
//           key: REACT_APP_RAZORPAY_KEY,
//           amount: Math.round(totalAmount * 100),
//           currency: 'INR',
//           name: 'Pagoda Hotel',
//           description: isPayAll ? 'Multiple Billings Payment' : selectedBilling.billing_type,
//           order_id: orderData.razorpay_order_id,
//           handler: async function (response) {
//             const paymentDetails = {
//               razorpay_order_id: response.razorpay_order_id,
//               razorpay_payment_id: response.razorpay_payment_id,
//               razorpay_signature: response.razorpay_signature,
//               billing_ids: billingIds,
//               booking_id: selectedBooking.bookingId,
//             };

//             try {
//               const verificationResponse = await fetch(
//                 `${BQ_BASE_URL}/bq/api/razorpay/verify_payment_multiple`,
//                 {
//                   method: 'POST',
//                   headers: {
//                     'Content-Type': 'application/json',
//                     Accept: 'application/json',
//                   },
//                   body: JSON.stringify(paymentDetails),
//                 }
//               );

//               if (verificationResponse.ok) {
//                 toast.success(
//                   <div className="flex items-center gap-2">
//                     <FiCheckCircle className="text-green-500" size={18} />
//                     <div>
//                       <p className="font-medium">Payment Successful!</p>
//                       <p className="text-sm text-gray-500">
//                         Transaction ID: {response.razorpay_payment_id}
//                       </p>
//                     </div>
//                   </div>,
//                   { autoClose: 5000 }
//                 );

//                 fetchData(true);
//                 setShowOnlinePaymentModal(false);
//                 setSelectedBilling(null);
//               } else {
//                 toast.error(
//                   <div className="flex items-center gap-2">
//                     <FiAlertCircle className="text-red-500" size={18} />
//                     <span>Payment verification failed. Please contact support.</span>
//                   </div>
//                 );
//               }
//             } catch (error) {
//               console.error('Verification error:', error);
//               toast.error(
//                 <div className="flex items-center gap-2">
//                   <FiAlertCircle className="text-red-500" size={18} />
//                   <span>Error verifying payment. Please try again.</span>
//                 </div>
//               );
//             }
//           },
//           modal: {
//             ondismiss: function () {
//               setProcessingPaymentId(null);
//             },
//           },
//           prefill: {
//             name: selectedBooking.guestName,
//             contact: selectedBooking.guestPhone?.toString(),
//           },
//           theme: {
//             color: '#3399cc',
//           },
//           notes: {
//             bookingId: selectedBooking.bookingId,
//             billingIds: billingIds.join(','),
//           },
//         };

//         const rzp = new window.Razorpay(options);
//         rzp.open();
//       }
//     } catch (error) {
//       console.error('Payment error:', error);
//       toast.error(
//         <div className="flex items-center gap-2">
//           <FiAlertCircle className="text-red-500" size={18} />
//           <span>{error.message || 'Error processing payment. Please try again.'}</span>
//         </div>
//       );
//     } finally {
//       setProcessingPaymentId(null);
//     }
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return 'N/A';
//     const date = new Date(dateString);
//     return date.toLocaleString();
//   };

//   const getReservationStatus = (status) => {
//     switch (status?.toLowerCase()) {
//       case 'hard':
//         return 'Checked In';
//       case 'soft':
//         return 'Reserved';
//       case 'checkedout':
//         return 'Checked Out';
//       case 'cancelled':
//         return 'Cancelled';
//       default:
//         return status || 'N/A';
//     }
//   };

//   const getStatusBadge = (status) => {
//     switch (status?.toLowerCase()) {
//       case 'hard':
//         return 'bg-green-100 text-green-800';
//       case 'soft':
//         return 'bg-yellow-100 text-yellow-800';
//       case 'checkedout':
//         return 'bg-blue-100 text-blue-800';
//       case 'cancelled':
//         return 'bg-red-100 text-red-800';
//       default:
//         return 'bg-gray-100 text-gray-800';
//     }
//   };

//   const TableSkeleton = () => (
//     <>
//       {[...Array(9)].map((_, index) => (
//         <tr key={index} className="animate-pulse">
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-8"></div>
//           </td>
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-32"></div>
//           </td>
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-24"></div>
//           </td>
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-28"></div>
//           </td>
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-16"></div>
//           </td>
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-20"></div>
//           </td>
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-16"></div>
//           </td>
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-24"></div>
//           </td>
//           <td className="py-3 px-4 border">
//             <div className="h-4 bg-gray-200 rounded w-24"></div>
//           </td>
//           <td className="py-2 px-1 border">
//             <div className="h-8 bg-gray-200 rounded w-32"></div>
//           </td>
//         </tr>
//       ))}
//     </>
//   );

//   const InfoModal = ({ message, onClose }) => (
//     <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
//       <div className="bg-white p-6 rounded-lg shadow-xl w-[400px] transform transition-all">
//         <div className="flex items-center gap-3 mb-4">
//           <div className="bg-blue-100 p-2 rounded-full">
//             <FiInfo className="text-blue-600" size={24} />
//           </div>
//           <h2 className="text-xl text-black font-bold">Information</h2>
//         </div>
//         <p className="text-gray-700 mb-6">{message}</p>
//         <div className="flex justify-end">
//           <button
//             className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition font-medium"
//             onClick={onClose}
//           >
//             Got it
//           </button>
//         </div>
//       </div>
//     </div>
//   );

//   const ViewDetailsModal = ({ booking, onClose }) => (
//     <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
//       <div className="bg-white p-6 rounded-lg shadow-xl w-[900px] max-h-[80vh] overflow-y-auto transform transition-all">
//         <div className="flex justify-between items-center mb-4">
//           <div className="flex items-center gap-3">
//             <div className="bg-blue-100 p-2 rounded-full">
//               <FiEye className="text-blue-600" size={24} />
//             </div>
//             <h2 className="text-xl text-black font-bold">Billing Details</h2>
//           </div>
//           <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//             <FiX size={24} />
//           </button>
//         </div>

//         <div className="mb-4 p-4 bg-gray-50 rounded-lg">
//           <div className="grid grid-cols-2 gap-4">
//             <div>
//               <p className="text-sm text-gray-600">Guest Name</p>
//               <p className="font-medium">{booking.guestName}</p>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Reservation No.</p>
//               <p className="font-mono font-medium">{booking.orderId}</p>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Reservation Status</p>
//               <span
//                 className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(booking.bookingStatus)}`}
//               >
//                 {getReservationStatus(booking.bookingStatus)}
//               </span>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Room Type</p>
//               <p className="font-medium">{booking.roomType || 'N/A'}</p>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Check In</p>
//               <p className="font-medium">{formatDate(booking.checkinDate)}</p>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Check Out</p>
//               <p className="font-medium">{formatDate(booking.checkoutDate)}</p>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Total Amount</p>
//               <p className="font-medium">₹{booking.grossBilled?.toFixed(2)}</p>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Amount Paid</p>
//               <p className="font-medium text-green-600">₹{booking.totalPaid?.toFixed(2)}</p>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Net Amount Due</p>
//               <p className="font-medium text-blue-600">₹{booking.netPayable?.toFixed(2)}</p>
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Total Discount</p>
//               <p className="font-medium text-purple-600">₹{booking.totalDiscount?.toFixed(2)}</p>
//             </div>
//           </div>
//           {booking.hasAdjustments && booking.totalAdjustmentAmount < 0 && (
//             <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
//               <p className="text-sm text-yellow-700">
//                 <span className="font-medium">Note:</span> Manual adjustments have been applied. Net
//                 amount due is ₹{booking.netPayable?.toFixed(2)} (after ₹
//                 {Math.abs(booking.totalAdjustmentAmount).toFixed(2)} adjustment).
//               </p>
//             </div>
//           )}
//           {booking.hasAdjustments && booking.totalAdjustmentAmount > 0 && (
//             <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
//               <p className="text-sm text-blue-700">
//                 <span className="font-medium">Note:</span> Additional adjustments have been applied.
//                 Net amount due is ₹{booking.netPayable?.toFixed(2)} (after ₹
//                 {booking.totalAdjustmentAmount.toFixed(2)} adjustment).
//               </p>
//             </div>
//           )}
//         </div>

//         <h3 className="font-semibold mb-2">Billings</h3>
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-sm">
//             <thead className="bg-gray-100">
//               <tr>
//                 <th className="px-2 py-2 text-left">Billing Type</th>
//                 <th className="px-2 py-2 text-right">Amount</th>
//                 <th className="px-2 py-2 text-right">Tax</th>
//                 <th className="px-2 py-2 text-right">Discount</th>
//                 <th className="px-2 py-2 text-right">Total</th>
//                 <th className="px-2 py-2 text-center">Status</th>
//                 <th className="px-2 py-2 text-center">Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {booking.billings
//                 .filter((b) => b.total_amount > 0 && !b.is_credit)
//                 .map((billing) => {
//                   const isPaid = billing.is_paid;

//                   return (
//                     <tr key={billing.billing_id} className="border-b hover:bg-gray-50">
//                       <td className="px-2 py-2">{billing.billing_type}</td>
//                       <td className="px-2 py-2 text-right">
//                         ₹{billing.base_amount?.toFixed(2) || 0}
//                       </td>
//                       <td className="px-2 py-2 text-right">
//                         ₹{billing.tax_amount?.toFixed(2) || 0}
//                       </td>
//                       <td className="px-2 py-2 text-right text-purple-600">
//                         ₹{billing.discount?.toFixed(2) || 0}
//                       </td>
//                       <td className="px-2 py-2 text-right font-medium">
//                         ₹{billing.total_amount?.toFixed(2) || 0}
//                       </td>
//                       <td className="px-2 py-2 text-center">
//                         <span
//                           className={`px-2 py-1 rounded-full text-xs ${
//                             isPaid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
//                           }`}
//                         >
//                           {isPaid ? 'Paid' : 'Pending'}
//                         </span>
//                       </td>
//                       <td className="px-2 py-2 text-center">
//                         {isPaid ? (
//                           <span className="text-gray-500 text-xs italic bg-gray-100 px-2 py-1 rounded">
//                             No actions
//                           </span>
//                         ) : booking.netPayable <= 0 ? (
//                           <span className="text-green-600 text-xs font-medium bg-green-50 px-2 py-1 rounded">
//                             Fully Paid
//                           </span>
//                         ) : (
//                           <div className="flex gap-1 justify-center">
//                             <button
//                               className="px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800"
//                               onClick={() => {
//                                 onClose();
//                                 handlePaySingleCash(billing, booking);
//                               }}
//                             >
//                               Cash
//                             </button>
//                             <button
//                               className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
//                               onClick={() => {
//                                 onClose();
//                                 handlePaySingleOnline(billing, booking);
//                               }}
//                             >
//                               Digital
//                             </button>
//                           </div>
//                         )}
//                       </td>
//                     </tr>
//                   );
//                 })}
//               {booking.billings.filter((b) => b.total_amount > 0 && !b.is_credit).length === 0 && (
//                 <tr>
//                   <td colSpan="7" className="text-center py-4 text-gray-500">
//                     No pending billings
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>

//         {booking.billings.some((b) => b.total_amount < 0 && b.is_credit) && (
//           <>
//             <h3 className="font-semibold mt-4 mb-2 text-orange-600">Adjustments / Credits</h3>
//             <div className="overflow-x-auto">
//               <table className="min-w-full text-sm">
//                 <thead className="bg-orange-50">
//                   <tr>
//                     <th className="px-2 py-2 text-left">Billing Type</th>
//                     <th className="px-2 py-2 text-right">Amount</th>
//                     <th className="px-2 py-2 text-right">Tax</th>
//                     <th className="px-2 py-2 text-right">Discount</th>
//                     <th className="px-2 py-2 text-right">Total</th>
//                     <th className="px-2 py-2 text-center">Status</th>
//                     <th className="px-2 py-2 text-center">Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {booking.billings
//                     .filter((b) => b.total_amount < 0 && b.is_credit)
//                     .map((billing) => {
//                       const isPaid = billing.is_paid;

//                       return (
//                         <tr
//                           key={billing.billing_id}
//                           className="border-b hover:bg-orange-50 bg-orange-50/30"
//                         >
//                           <td className="px-2 py-2 text-orange-700">{billing.billing_type}</td>
//                           <td className="px-2 py-2 text-right text-orange-700">
//                             ₹{billing.base_amount?.toFixed(2) || 0}
//                           </td>
//                           <td className="px-2 py-2 text-right text-orange-700">
//                             ₹{billing.tax_amount?.toFixed(2) || 0}
//                           </td>
//                           <td className="px-2 py-2 text-right text-purple-600">
//                             ₹{billing.discount?.toFixed(2) || 0}
//                           </td>
//                           <td className="px-2 py-2 text-right font-medium text-red-600">
//                             ₹{billing.total_amount?.toFixed(2) || 0}
//                           </td>
//                           <td className="px-2 py-2 text-center">
//                             <span
//                               className={`px-2 py-1 rounded-full text-xs ${
//                                 isPaid
//                                   ? 'bg-green-100 text-green-800'
//                                   : 'bg-yellow-100 text-yellow-800'
//                               }`}
//                             >
//                               {isPaid ? 'Paid' : 'Pending'}
//                             </span>
//                           </td>
//                           <td className="px-2 py-2 text-center">
//                             <span className="text-gray-500 text-xs italic bg-gray-100 px-2 py-1 rounded">
//                               Credit applied
//                             </span>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                 </tbody>
//               </table>
//             </div>
//           </>
//         )}

//         {booking.allPayments && booking.allPayments.length > 0 && (
//           <>
//             <h3 className="font-semibold mt-4 mb-2">Payment History</h3>
//             <div className="overflow-x-auto">
//               <table className="min-w-full text-sm">
//                 <thead className="bg-gray-100">
//                   <tr>
//                     <th className="px-2 py-2 text-left">Date</th>
//                     <th className="px-2 py-2 text-left">Method</th>
//                     <th className="px-2 py-2 text-right">Amount</th>
//                     <th className="px-2 py-2 text-center">Status</th>
//                     <th className="px-2 py-2 text-center">Transaction ID</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {booking.allPayments.map((payment, idx) => (
//                     <tr key={idx} className="border-b hover:bg-gray-50">
//                       <td className="px-2 py-2">
//                         {formatDate(payment.date || payment.payment_date)}
//                       </td>
//                       <td className="px-2 py-2">{payment.payment_method || 'Cash'}</td>
//                       <td className="px-2 py-2 text-right">
//                         ₹{parseFloat(payment.amount).toFixed(2)}
//                       </td>
//                       <td className="px-2 py-2 text-center">
//                         <span
//                           className={`px-2 py-1 rounded-full text-xs ${
//                             payment.status === 'Success'
//                               ? 'bg-green-100 text-green-800'
//                               : 'bg-yellow-100 text-yellow-800'
//                           }`}
//                         >
//                           {payment.status || 'Success'}
//                         </span>
//                       </td>
//                       <td className="px-2 py-2 text-center font-mono text-xs">
//                         {payment.transaction_id || payment.payment_id || '-'}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </>
//         )}

//         <div className="flex justify-end mt-4">
//           <button
//             className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition font-medium"
//             onClick={onClose}
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );

//   return (
//     <div className="p-6 bg-gray-50 min-h-screen flex flex-col">
//       <ToastContainer
//         position="top-right"
//         autoClose={3000}
//         hideProgressBar={false}
//         newestOnTop
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />

//       {showInfoModal && <InfoModal message={infoMessage} onClose={() => setShowInfoModal(false)} />}

//       {showViewModal && selectedBooking && (
//         <ViewDetailsModal
//           booking={selectedBooking}
//           onClose={() => {
//             setShowViewModal(false);
//             setSelectedBooking(null);
//           }}
//         />
//       )}

//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-operations')
//             }
//             className="btn"
//           >
//             Front Desk Operations
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/guest-managment')
//             }
//             className="btn"
//           >
//             Guest Management
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-roomservices')
//             }
//             className="btn"
//           >
//             Hospitality Services
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-amenities')
//             }
//             className="btn"
//           >
//             Facility Services
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/billing-payments')
//             }
//             className="btn text-black font-bold"
//           >
//             Billing & Payments
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/refunds-adjustments')
//             }
//             className="btn"
//           >
//             Refunds & Adjustments
//           </button>
//         </div>
//       </header>

//       <div className="text-center mb-4">
//         <div className="inline-block">
//           <h1 className="text-2xl font-bold text-center text-black">Billings & Payments</h1>
//           <p className="text-sm font-poppins text-center text-gray-600 max-w-2xl">
//             Make payments via cash or digital methods, view transaction history, and manage payments
//             seamlessly.
//           </p>
//           <hr className="border-gray-400 mt-2 w-60 mx-auto" />
//         </div>
//       </div>

//       <div className="mb-4 flex flex-col md:flex-row items-center gap-4">
//         <div className="relative w-full md:w-2/5">
//           <div className="relative">
//             <input
//               type="text"
//               placeholder="Search by guest name or reservation number"
//               value={searchTerm}
//               onChange={handleSearch}
//               className="p-3 pl-12 pr-10 h-12 border w-full border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
//             />
//             <FiSearch className="absolute top-3.5 left-4 text-gray-500" size={20} />
//             {searchTerm && !isSearching && (
//               <button
//                 onClick={clearSearch}
//                 className="absolute top-3.5 right-3 text-gray-500 hover:text-gray-700 transition-colors"
//                 title="Clear search"
//               >
//                 <FiX size={20} />
//               </button>
//             )}
//             {isSearching && (
//               <div className="absolute top-3.5 right-3">
//                 <FiLoader className="animate-spin text-gray-500" size={18} />
//               </div>
//             )}
//           </div>
//         </div>

//         <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end ml-auto">
//           <button
//             onClick={handleRefresh}
//             disabled={isRefreshing}
//             className="p-3 h-12 px-4 border rounded-lg shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
//             title="Refresh data"
//           >
//             <FiRefreshCw className={`${isRefreshing ? 'animate-spin' : ''}`} size={18} />
//             <span className="hidden sm:inline">Refresh</span>
//           </button>

//           <select
//             onChange={handleTimeRangeChange}
//             value={timeRange}
//             className="p-3 h-12 border rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-black"
//           >
//             <option value="all">All Time</option>
//             <option value="day">Today</option>
//             <option value="week">This Week</option>
//             <option value="month">This Month</option>
//           </select>

//           <select
//             onChange={handleBillingTypeChange}
//             value={selectedBillingType}
//             className="p-3 h-12 border rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-black"
//           >
//             <option value="all">All Billing Types</option>
//             {billingTypes.map((type, index) => (
//               <option key={index} value={type}>
//                 {type}
//               </option>
//             ))}
//           </select>
//         </div>
//       </div>

//       <div className="overflow-x-auto bg-white shadow-md rounded-lg">
//         <table className="min-w-full bg-white border border-gray-300">
//           <thead>
//             <tr className="bg-gray-200 text-black font-semibold text-base">
//               <th className="py-3 px-1 border border-gray-300">Sr No.</th>
//               <th className="py-3 px-4 border border-gray-300">Guest</th>
//               <th className="py-3 px-4 border border-gray-300">Reservation No.</th>
//               <th className="py-3 px-4 border border-gray-300">Total Amount</th>
//               <th className="py-3 px-4 border border-gray-300">Amount Paid</th>
//               <th className="py-3 px-4 border border-gray-300">Discount</th>
//               <th className="py-3 px-4 border border-gray-300">Amount Due</th>
//               <th className="py-3 px-4 border border-gray-300">Reservation Status</th>
//               <th className="py-3 px-4 border border-gray-300">Date</th>
//               <th className="py-3 px-2 border border-gray-300">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {isLoading ? (
//               <TableSkeleton />
//             ) : currentRows.length > 0 ? (
//               currentRows.map((item, index) => {
//                 const isProcessing = processingPaymentId === item.id;
//                 const hasUnpaid = item.unpaidBillingIds.length > 0 && item.netPayable > 0;

//                 return (
//                   <tr key={item.id} className="hover:bg-gray-50 border transition-colors">
//                     <td className="py-2 px-2 border text-center">{indexOfFirstRow + index + 1}</td>
//                     <td className="py-2 px-4 border text-sm font-medium">{item.guestName}</td>
//                     <td className="py-2 px-4 border font-mono text-sm">{item.orderId}</td>
//                     <td className="py-2 px-4 border font-medium">
//                       ₹{item.grossBilled?.toFixed(2)}
//                     </td>
//                     <td className="py-2 px-4 border text-green-600 font-medium">
//                       ₹{item.totalPaid?.toFixed(2)}
//                     </td>
//                     <td className="py-2 px-4 border text-purple-600 font-medium">
//                       ₹{item.totalDiscount?.toFixed(2)}
//                     </td>
//                     <td className="py-2 px-4 border font-medium text-blue-600">
//                       ₹{item.netPayable?.toFixed(2)}
//                     </td>
//                     <td className="py-2 px-4 border">
//                       <span
//                         className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(item.bookingStatus)}`}
//                       >
//                         {getReservationStatus(item.bookingStatus)}
//                       </span>
//                     </td>
//                     <td className="py-2 px-4 border text-sm">{formatDate(item.date)}</td>
//                     <td className="py-2 px-1 border">
//                       {item.netPayable > 0 && hasUnpaid ? (
//                         <div className="flex flex-col gap-1">
//                           <div className="flex gap-1">
//                             <button
//                               className="px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 flex-1 flex items-center justify-center gap-1"
//                               onClick={() => handlePayAllCash(item)}
//                               disabled={isProcessing}
//                             >
//                               {isProcessing ? (
//                                 <FiLoader className="animate-spin" size={12} />
//                               ) : (
//                                 <FaRupeeSign size={12} />
//                               )}
//                               <span>Pay All</span>
//                             </button>
//                             <button
//                               className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex-1 flex items-center justify-center gap-1"
//                               onClick={() => handlePayAllOnline(item)}
//                               disabled={isProcessing}
//                             >
//                               {isProcessing ? (
//                                 <FiLoader className="animate-spin" size={12} />
//                               ) : (
//                                 <FiCreditCard size={12} />
//                               )}
//                               <span>Digital</span>
//                             </button>
//                           </div>
//                           <button
//                             className="px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 flex items-center justify-center gap-1"
//                             onClick={() => handleViewDetails(item)}
//                           >
//                             <FiEye size={12} />
//                             <span>View</span>
//                           </button>
//                         </div>
//                       ) : item.totalBilled > 0 ? (
//                         <div className="flex flex-col gap-1">
//                           <span className="text-green-600 font-medium text-xs bg-green-50 px-2 py-1 rounded text-center border border-green-200">
//                             Fully Paid
//                           </span>
//                           <button
//                             className="px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 flex items-center justify-center gap-1"
//                             onClick={() => handleViewDetails(item)}
//                           >
//                             <FiEye size={12} />
//                             <span>View</span>
//                           </button>
//                         </div>
//                       ) : (
//                         <span className="text-gray-500 text-xs italic bg-gray-100 px-2 py-1 rounded block text-center">
//                           No action
//                         </span>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })
//             ) : (
//               <tr>
//                 <td colSpan="10" className="text-center py-12">
//                   <div className="flex flex-col items-center">
//                     <div className="bg-gray-100 rounded-full p-4 mb-3">
//                       <FiSearch size={24} className="text-gray-500" />
//                     </div>
//                     <p className="text-gray-500 text-lg mb-2">No billing records found</p>
//                     {searchTerm && (
//                       <button
//                         onClick={clearSearch}
//                         className="text-black hover:underline text-sm flex items-center gap-1"
//                       >
//                         <FiX size={14} />
//                         Clear search and filters
//                       </button>
//                     )}
//                   </div>
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {!isLoading && filteredData.length > 0 && (
//         <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
//           <div className="text-sm text-gray-600">
//             Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredData.length)} of{' '}
//             {filteredData.length} entries
//           </div>

//           <div className="flex items-center gap-1">
//             <button
//               onClick={handleFirstPage}
//               disabled={currentPage === 1}
//               className={`p-2 rounded-lg transition ${
//                 currentPage === 1
//                   ? 'text-gray-400 cursor-not-allowed'
//                   : 'text-gray-700 hover:bg-gray-200'
//               }`}
//               title="First page"
//             >
//               <FiChevronsLeft size={18} />
//             </button>

//             <button
//               onClick={handlePrevPage}
//               disabled={currentPage === 1}
//               className={`p-2 rounded-lg transition ${
//                 currentPage === 1
//                   ? 'text-gray-400 cursor-not-allowed'
//                   : 'text-gray-700 hover:bg-gray-200'
//               }`}
//               title="Previous page"
//             >
//               <FiChevronLeft size={18} />
//             </button>

//             {getVisiblePages().map((page) => (
//               <button
//                 key={page}
//                 onClick={() => handlePageChange(page)}
//                 className={`w-10 h-10 rounded-lg font-medium transition ${
//                   currentPage === page ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-200'
//                 }`}
//               >
//                 {page}
//               </button>
//             ))}

//             {totalPages > maxVisiblePages && currentPage < totalPages - 2 && (
//               <span className="text-gray-500">...</span>
//             )}

//             <button
//               onClick={handleNextPage}
//               disabled={currentPage === totalPages}
//               className={`p-2 rounded-lg transition ${
//                 currentPage === totalPages
//                   ? 'text-gray-400 cursor-not-allowed'
//                   : 'text-gray-700 hover:bg-gray-200'
//               }`}
//               title="Next page"
//             >
//               <FiChevronRight size={18} />
//             </button>

//             <button
//               onClick={handleLastPage}
//               disabled={currentPage === totalPages}
//               className={`p-2 rounded-lg transition ${
//                 currentPage === totalPages
//                   ? 'text-gray-400 cursor-not-allowed'
//                   : 'text-gray-700 hover:bg-gray-200'
//               }`}
//               title="Last page"
//             >
//               <FiChevronsRight size={18} />
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Cash Payment Modal */}
//       {showModal && selectedBooking && (
//         <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-xl w-[470px] transform transition-all">
//             <div className="flex items-center gap-3 mb-4">
//               <div className="bg-green-100 p-2 rounded-full">
//                 <FiCheckCircle className="text-green-600" size={24} />
//               </div>
//               <h2 className="text-xl text-black font-bold">Confirm Cash Payment</h2>
//             </div>

//             <div className="space-y-3 mb-6 bg-gray-50 p-4 rounded-lg">
//               <div className="flex justify-between">
//                 <p className="text-gray-600">Guest:</p>
//                 <p className="text-gray-800 font-medium">{selectedBooking.guestName}</p>
//               </div>

//               <div className="flex justify-between">
//                 <p className="text-gray-600">Reservation No.:</p>
//                 <p className="text-gray-800 font-mono font-medium">{selectedBooking.orderId}</p>
//               </div>

//               {selectedBilling ? (
//                 // Single Payment Mode
//                 <>
//                   <div className="flex justify-between">
//                     <p className="text-gray-600">Billing Type:</p>
//                     <p className="text-gray-800 font-medium">{selectedBilling.billing_type}</p>
//                   </div>

//                   {selectedBooking.hasAdjustments &&
//                     selectedBooking.totalAdjustmentAmount < 0 &&
//                     selectedBilling.total_amount > 0 &&
//                     !selectedBilling.is_credit && (
//                       <>
//                         <div className="flex justify-between">
//                           <p className="text-gray-600">Gross Amount:</p>
//                           <p className="text-gray-800 font-medium">
//                             ₹{selectedBilling.total_amount?.toFixed(2)}
//                           </p>
//                         </div>
//                         <div className="flex justify-between">
//                           <p className="text-gray-600">Adjustments:</p>
//                           <p className="text-red-600 font-medium">
//                             -₹
//                             {Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}
//                           </p>
//                         </div>
//                         <div className="flex justify-between border-t border-gray-300 pt-2">
//                           <p className="text-gray-700 font-medium">Net Amount Due:</p>
//                           <p className="text-green-600 font-bold text-lg">
//                             ₹{selectedBooking.netPayable?.toFixed(2)}
//                           </p>
//                         </div>
//                       </>
//                     )}

//                   {(!selectedBooking.hasAdjustments ||
//                     selectedBilling.total_amount < 0 ||
//                     selectedBilling.is_credit) && (
//                     <div className="flex justify-between border-t border-gray-300 pt-2">
//                       <p className="text-gray-700 font-medium">Amount Due:</p>
//                       <p className="text-green-600 font-bold text-lg">
//                         ₹{selectedBilling.total_amount?.toFixed(2)}
//                       </p>
//                     </div>
//                   )}
//                 </>
//               ) : (
//                 // Pay All Mode
//                 <>
//                   <div className="flex justify-between">
//                     <p className="text-gray-600">Gross Amount:</p>
//                     <p className="text-gray-800 font-medium">
//                       ₹{selectedBooking.grossBilled?.toFixed(2)}
//                     </p>
//                   </div>
//                   {selectedBooking.hasAdjustments && selectedBooking.totalAdjustmentAmount < 0 && (
//                     <div className="flex justify-between">
//                       <p className="text-gray-600">Adjustments:</p>
//                       <p className="text-red-600 font-medium">
//                         -₹
//                         {Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}
//                       </p>
//                     </div>
//                   )}
//                   <div className="flex justify-between border-t border-gray-300 pt-2">
//                     <p className="text-gray-700 font-medium">Total Amount Due:</p>
//                     <p className="text-green-600 font-bold text-lg">
//                       ₹{selectedBooking.netPayable?.toFixed(2)}
//                     </p>
//                   </div>
//                 </>
//               )}
//             </div>

//             <div className="flex justify-end gap-3">
//               <button
//                 className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
//                 onClick={() => {
//                   setShowModal(false);
//                   setSelectedBilling(null);
//                 }}
//               >
//                 Cancel
//               </button>
//               <button
//                 className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] justify-center"
//                 onClick={handleContinueCash}
//                 disabled={processingPaymentId === selectedBooking.id}
//               >
//                 {processingPaymentId === selectedBooking.id ? (
//                   <>
//                     <FiLoader className="animate-spin" size={18} />
//                     <span>Processing...</span>
//                   </>
//                 ) : (
//                   'Confirm Payment'
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Add Billing Record Modal */}
//       {showAddBillingModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-xl w-[500px] transform transition-all max-h-[90vh] overflow-y-auto">
//             <div className="flex items-center gap-3 mb-5">
//               <div className="bg-blue-100 p-2 rounded-full">
//                 <FiPlus className="text-blue-600" size={24} />
//               </div>
//               <h2 className="text-xl text-black font-bold">Add Billing Record</h2>
//             </div>

//             <div className="space-y-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Booking ID <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="text"
//                   placeholder="Enter booking ID"
//                   value={billingFormData.bookingId}
//                   onChange={(e) =>
//                     setBillingFormData({
//                       ...billingFormData,
//                       bookingId: e.target.value,
//                     })
//                   }
//                   className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Base Amount <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="number"
//                   placeholder="Enter base amount"
//                   value={billingFormData.baseAmount}
//                   onChange={(e) =>
//                     setBillingFormData({
//                       ...billingFormData,
//                       baseAmount: e.target.value,
//                     })
//                   }
//                   className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">Tax Amount</label>
//                 <input
//                   type="number"
//                   placeholder="Enter tax amount"
//                   value={billingFormData.taxAmount}
//                   onChange={(e) =>
//                     setBillingFormData({
//                       ...billingFormData,
//                       taxAmount: e.target.value,
//                     })
//                   }
//                   className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
//                 <input
//                   type="number"
//                   placeholder="Enter discount amount"
//                   value={billingFormData.discount}
//                   onChange={(e) =>
//                     setBillingFormData({
//                       ...billingFormData,
//                       discount: e.target.value,
//                     })
//                   }
//                   className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Billing Type <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="text"
//                   value={billingFormData.billingType}
//                   disabled
//                   className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
//                 />
//                 <p className="text-xs text-gray-500 mt-1">
//                   This field is pre-set to "Food Order" and will be sent to the backend.
//                 </p>
//               </div>
//             </div>

//             <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
//               <button
//                 className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
//                 onClick={() => {
//                   setShowAddBillingModal(false);
//                   setBillingFormData({
//                     bookingId: '',
//                     baseAmount: '',
//                     taxAmount: '',
//                     discount: '',
//                     billingType: 'Food Order',
//                   });
//                 }}
//               >
//                 Cancel
//               </button>
//               <button
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition font-medium flex items-center gap-2"
//                 onClick={handleAddBilling}
//               >
//                 <FiPlus size={16} />
//                 Add Record
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Online Payment Modal */}
//       {showOnlinePaymentModal && selectedBooking && (
//         <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-xl w-[470px] transform transition-all">
//             <div className="flex items-center gap-3 mb-4">
//               <div className="bg-blue-100 p-2 rounded-full">
//                 <FiInfo className="text-blue-600" size={24} />
//               </div>
//               <h2 className="text-xl text-black font-bold">Confirm Online Payment</h2>
//             </div>

//             <div className="space-y-3 mb-6 bg-gray-50 p-4 rounded-lg">
//               <div className="flex justify-between">
//                 <p className="text-gray-600">Guest:</p>
//                 <p className="text-gray-800 font-medium">{selectedBooking.guestName}</p>
//               </div>

//               <div className="flex justify-between">
//                 <p className="text-gray-600">Reservation No.:</p>
//                 <p className="text-gray-800 font-mono font-medium">{selectedBooking.orderId}</p>
//               </div>

//               {selectedBilling ? (
//                 <>
//                   <div className="flex justify-between">
//                     <p className="text-gray-600">Billing Type:</p>
//                     <p className="text-gray-800 font-medium">{selectedBilling.billing_type}</p>
//                   </div>

//                   {selectedBooking.hasAdjustments &&
//                     selectedBooking.totalAdjustmentAmount < 0 &&
//                     selectedBilling.total_amount > 0 &&
//                     !selectedBilling.is_credit && (
//                       <>
//                         <div className="flex justify-between">
//                           <p className="text-gray-600">Gross Amount:</p>
//                           <p className="text-gray-800 font-medium">
//                             ₹{selectedBilling.total_amount?.toFixed(2)}
//                           </p>
//                         </div>
//                         <div className="flex justify-between">
//                           <p className="text-gray-600">Adjustments:</p>
//                           <p className="text-red-600 font-medium">
//                             -₹
//                             {Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}
//                           </p>
//                         </div>
//                         <div className="flex justify-between border-t border-gray-300 pt-2">
//                           <p className="text-gray-700 font-medium">Net Amount Due:</p>
//                           <p className="text-green-600 font-bold text-lg">
//                             ₹{selectedBooking.netPayable?.toFixed(2)}
//                           </p>
//                         </div>
//                       </>
//                     )}

//                   {(!selectedBooking.hasAdjustments ||
//                     selectedBilling.total_amount < 0 ||
//                     selectedBilling.is_credit) && (
//                     <div className="flex justify-between border-t border-gray-300 pt-2">
//                       <p className="text-gray-700 font-medium">Amount Due:</p>
//                       <p className="text-green-600 font-bold text-lg">
//                         ₹{selectedBilling.total_amount?.toFixed(2)}
//                       </p>
//                     </div>
//                   )}
//                 </>
//               ) : (
//                 <>
//                   <div className="flex justify-between">
//                     <p className="text-gray-600">Gross Amount:</p>
//                     <p className="text-gray-800 font-medium">
//                       ₹{selectedBooking.grossBilled?.toFixed(2)}
//                     </p>
//                   </div>
//                   {selectedBooking.hasAdjustments && selectedBooking.totalAdjustmentAmount < 0 && (
//                     <div className="flex justify-between">
//                       <p className="text-gray-600">Adjustments:</p>
//                       <p className="text-red-600 font-medium">
//                         -₹
//                         {Math.abs(selectedBooking.totalAdjustmentAmount).toFixed(2)}
//                       </p>
//                     </div>
//                   )}
//                   <div className="flex justify-between border-t border-gray-300 pt-2">
//                     <p className="text-gray-700 font-medium">Total Amount Due:</p>
//                     <p className="text-green-600 font-bold text-lg">
//                       ₹{selectedBooking.netPayable?.toFixed(2)}
//                     </p>
//                   </div>
//                 </>
//               )}
//             </div>

//             <div className="flex justify-end gap-3">
//               <button
//                 className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
//                 onClick={() => {
//                   setShowOnlinePaymentModal(false);
//                   setSelectedBilling(null);
//                 }}
//               >
//                 Cancel
//               </button>
//               <button
//                 className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] justify-center"
//                 onClick={handleContinueOnline}
//                 disabled={processingPaymentId === selectedBooking.id}
//               >
//                 {processingPaymentId === selectedBooking.id ? (
//                   <>
//                     <FiLoader className="animate-spin" size={18} />
//                     <span>Processing...</span>
//                   </>
//                 ) : (
//                   'Pay Now'
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Cash;
